import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

// styles
import './style.scss';

// antd component
import { Form, Radio, Button, Input, Upload, message, Row, Col } from 'antd';

// components
import CustomDate from 'components/Forms/Fields/CustomDate';
import TextField from 'components/Forms/Fields/TextField';
import CustomTime from 'components/Forms/Fields/CustomTime';
import CustomSelect from 'components/Forms/Fields/CustomSelect';
import PreviewMedia from 'components/Forms/Fields/PreviewMedia';

// containers
import UploadMedia from 'mobile/containers/UploadMedia';
import SelectFieldAxios from 'containers/SelectFieldAxios';

// global user
import { useGetLocalUser } from 'engine/Contexts/User.context';

// utils
import { normFile } from 'utils/uploadHelper';
import {
  DISPENSARY_PROGRAM,
  ACTIVITY_REASON,
  DAY_OF_WEEK,
  RECURRING,
} from 'utils/helpers/helper.constant';
import {
  getStatusesList,
  getTypeList,
} from 'utils/helpers/helper.generateData';
import { clean, getArrayIds, sortByKey, getObjId } from 'utils/helpers/helper.general';
// helper for forms
import {
  setTimeRangeAfterChanges,
  setDateRangeAfterChanges,
} from 'utils/forms/forms.utils';
// hooks
import { useRefState } from 'utils/hooks';

function CreateAbsence({
  absence,
  isNew,
  // actions
  change,
  // default
  className,
}) {
  const childClassName = classNames('create-absence-wrapper', className);

  const isEdit = !isNew;
  const timeFormat = 'HH:mm';
  const dateFormat = 'DD.MM.YYYY';

  const localUser = useGetLocalUser()();

  const now = moment(absence?.date) || moment();

  let initAbsence = {
    status: 'pending',
    type: 'medical',
    created_by: localUser?._id || null,
    duration: 'day',
    date: moment(now, dateFormat),
    courses: [],
    // for duration more than 1 day
    date_from: null,
    time_from: null,
    date_to: null,
    time_to: null,
    // For  "Absence récurrente" part
    recurring: null,
    recurring_date: null,
  };

  const [absenceInfo, setAbsenceInfo] = useState(absence || initAbsence);
  const [absenceType, setAbsenceType] = useState(
    absence?.type || initAbsence?.type,
  );
  const [absenceStatus, setAbsenceStatus] = useState(
    absence?.status || initAbsence?.status,
  );

  const [isDisabled, setIsDisabled] = useState(false);
  const [durationType, setDurationType] = useState(initAbsence?.duration);
  const [endponitObj, setEndponitObj] = useState(false);
  const [currentDay, setCurrentDay] = useState(now);

  const [hasCourse, setHasCourse] = useState(false);
  const [currentCourse, currentCourseRef, setCurrentCourse] = useRefState([]);

  const [currentStudent, currentStudentRef, setCurrentStudent] = useRefState(false);
  // only for teacher mode
  const [studentParent, studentParentRef, setStudentParent] = useRefState(
    false,
  );

  // for medical, no-medical
  const recurringOption = RECURRING;
  const [recurringType, setRecurringType] = useState('every_week');
  const [isRepeate, setIsRepeate] = useState(false);
  const [hasRecur, setHasRecur] = useState(false);

  // for media
  const [mediaFiles, mediaFilesRef, setMediaFiles] = useRefState([]);
  const [uploading, setUploading] = useState(false);

  const [AbsFormCreate] = Form.useForm();

  // Clearing the state after unmounting a component
  useEffect(() => {
    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state for SelectFieldAxios component
      setHasCourse(false);
      setCurrentCourse([]);
      setEndponitObj(false);
      setCurrentStudent(false);
      setStudentParent(false);
      setAbsenceInfo(false);
    };
  }, []);

  /**
   * Change total amount Step by Absence Type
   */
  useEffect(() => {
    if (absence !== null && absence) {
      const hasValFormData = clean(absence);
      setIsDisabled(false);
      setAbsenceInfo(absence);
      setAbsenceType(absence?.type);
      setAbsenceStatus(absence?.status);
      setDurationType(hasValFormData?.duration || initAbsence?.duration);

      AbsFormCreate.setFieldsValue({
        type: absence?.type,
        duration: absence?.duration || initAbsence?.duration,
        reason: absence?.reason || null,
        status: absence?.status || initAbsence?.status,
        student: absence?.student?._id || absence?.student,
        class: absence?.student?.class_info?.title,
      });

      if (
        hasValFormData.hasOwnProperty('type') &&
        hasValFormData?.type !== 'sickness'
      ) {
        AbsFormCreate.setFieldsValue({
          // for duration more than 1 day
          date_from: moment(hasValFormData?.date_from) || null,
          time_from: moment(hasValFormData?.time_from) || null,
          date_to: moment(hasValFormData?.date_to) || null,
          time_to: moment(hasValFormData?.time_to) || null,
          // For  "Absence récurrente" part
          recurring: hasValFormData?.recurring || null,
          recurring_date: moment(hasValFormData?.recurring_date) || null,
        });
      }

      if (
        hasValFormData.hasOwnProperty('courses') &&
        hasValFormData.courses.length > 0
      ) {
        AbsFormCreate.setFieldsValue({
          courses: getArrayIds(hasValFormData.courses),
        });
      }

      if (hasValFormData.hasOwnProperty('recurring')) {
        setRecurringType(hasValFormData.recurring);
        setIsRepeate(true);
        setHasRecur(true);
      }

      if (hasValFormData.hasOwnProperty('date')) {
        setCurrentDay(moment(hasValFormData.date));
        setEndponitObj({
          course_week:
            DAY_OF_WEEK[moment(hasValFormData.date).isoWeekday() - 1]?.value,
        });
      } else {
        setCurrentDay(now);
        setEndponitObj({
          course_week: DAY_OF_WEEK[now.isoWeekday() - 1]?.value,
        });
      }

      if (hasValFormData?.student?._id) {
        setCurrentStudent(hasValFormData?.student);
        const contact_person =
          hasValFormData?.student?.contact_person?._id ||
          hasValFormData?.student?.contact_person;
        const parent =
          contact_person ||
          hasValFormData?.student?.parents[0]?._id ||
          hasValFormData?.student?.parents[0];
        setStudentParent(parent);
      }

      if (hasValFormData.hasOwnProperty('media')) {
        AbsFormCreate.setFieldsValue({
          media: hasValFormData?.media,
        });
        setMediaFiles(hasValFormData.media);
      }
    } else {
      setCurrentDay(now);
      setEndponitObj({
        course_week: DAY_OF_WEEK[now.isoWeekday() - 1]?.value,
      });
    }
  }, [absence]);

  /*
   * Check current date and day of the week
   * for generate Courses list
   */
  useEffect(() => {
    if (currentDay) {
      // 1 - Monday, 7 - Sunday
      const week = currentDay.isoWeekday();
      setEndponitObj({
        course_week: DAY_OF_WEEK[week - 1]?.value,
      });
    }
  }, [currentDay]);

  useEffect(() => {
    if (currentDay && isRepeate && !hasRecur) {
      const newDate = getRecurringDate(recurringType, currentDay);
      AbsFormCreate.setFieldsValue({
        recurring_date: newDate,
      });
    }
  }, [isRepeate, hasRecur, currentDay]);

  const resetInitFileds = useCallback(durationVal => {
    let initFileds = {};
    switch (durationVal) {
      case 'day': {
        initFileds = {
          date: moment(now, dateFormat),
          // for duration more than 1 day
          date_from: null,
          time_from: null,
          date_to: null,
          time_to: null,
        };
        break;
      }
      case 'more': {
        initFileds = {
          date: null,
          // for duration more than 1 day
          date_from: moment(now, dateFormat),
          time_from: moment(now, timeFormat),
          date_to: moment(now, dateFormat),
          time_to: moment(now, timeFormat),
        };
        break;
      }
    }
    AbsFormCreate.setFieldsValue({
      courses: [],
      // For  "Absence récurrente" part
      recurring: null,
      recurring_date: null,
      ...initFileds,
    });
    setHasRecur(false);
  }, [now, AbsFormCreate]);

  // Main result/save data
  const onFinish = values => {
    if (values.hasOwnProperty('courses')) {
      const coursesObj =
        values?.courses.length > 0 &&
        values?.courses
          .map(course => {
            let obj = null;
            currentCourseRef?.current &&
              currentCourseRef?.current.map(cc => {
                if (course == cc?._id || course?._id == cc?._id) {
                  obj = cc;
                }
              });
            return obj;
          })
          .filter(el => el !== null);
      values.courses = coursesObj || [];
    }

    if (values && values.hasOwnProperty('media')) {
      if (!values.media) {
        delete values.media;
        setMediaFiles([]);
      } else if (typeof values.media[0] !== 'string') {
        values.media = mediaFilesRef.current;
      }
    }

    if (values.hasOwnProperty('type')) {
      setAbsenceType(values.type);
    }

    let newObj = { ...absenceInfo, ...values };

    if (values?.type == 'sickness' && absenceInfo?.type !== 'sickness') {
      newObj.status = 'sick';
    }

    const hole_day_pass = durationType == 'day' && values?.courses?.length == 0;

    if (hole_day_pass) {
      newObj['hole_day_pass'] = hole_day_pass;
    }

    newObj.parent = studentParentRef?.current || null

    setAbsenceInfo(newObj);

    // update DB
    change(clean(newObj));

    AbsFormCreate.resetFields();
  };

  const getRecurringDate = useCallback((dateType, currentDate) => {
    if (dateType == 'every_day') {
      return moment(currentDate).add(1, 'days');
    } else if (dateType == 'every_week') {
      return moment(currentDate).add(7, 'days');
      //.endOf('isoWeek');
    } else if (dateType == 'every_month') {
      return moment(currentDate).add(1, 'months');
    }
  }, []);

  // Select courses by current child and current day of the week
  let selectCourses = useCallback(
    objSend => {
      // initial data for SelectFieldAxios component
      const newElementSelect = {
        label: 'Cours',
        name: 'courses',
        endponit: `students/courses/${getObjId(currentStudent)}`,
        required: hasCourse,
        displayLabel: true,
        type: 'axios-select',
        tagRender: props => (
          <span className="multiple-label-list">{props.label}</span>
        ),
        itemCallback: item => {
          return {
            key: item?._id,
            title: `<span classN="time">${moment(
              item?.course_time_start,
            ).format('HH:mm')}</span> <span class="cours-name">${
              item?.subject?.name
            }</span>`,
            label: `${item?.subject?.name}`,
            value: item?._id,
            color: item?.subject?.color,
            item: item,
          };
        },
      };

      return !!currentStudent && (
        <>
          <SelectFieldAxios
            mode="multiple"
            isSearch={false}
            sortBy="title"
            className="axios-select"
            placeholder="Selectionner le(s) cours"
            tagRender={newElementSelect.tagRender}
            endponit={newElementSelect.endponit}
            endponitSend={objSend}
            getResponse={response => {
              if (response?.data.length > 0) {
                setHasCourse(true);
                setCurrentCourse(response?.data);
              } else {
                setHasCourse(false);
              }
            }}
            itemCallback={newElementSelect.itemCallback}
            item={newElementSelect}
          />
          {!hasCourse && (
            <span className="alert axios-alert">
              Il n'y a pas de cours à cette date.
            </span>
          )}
        </>
      );
    },
    [currentStudent, hasCourse],
  );

  // Select student for current absence
  let selectStudents = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: 'Étudiant',
      name: 'student',
      endponit: `students/list/select`,
      endponitSend: {},
      required: true,
      value: '',
      displayLabel: true,
      type: 'axios-select',
      tagRender: props => (
        <span className="multiple-label-list">{props.label}</span>
      ),
      onSelect: (LabeledValue, option) => {
        setCurrentStudent(option?.item);
        AbsFormCreate.setFieldValue('class', option.item.class_info.title);
        const contact_person =
          option?.item?.contact_person?._id || option?.item?.contact_person;
        const parent =
          contact_person ||
          option?.item?.parents[0]?._id ||
          option?.item?.parents[0];
        setStudentParent(parent);
      },
      itemCallback: item => {
        return {
          key: item._id,
          label: `${item?.description?.lastname} ${
            item?.description?.firstname
          }`,
          value: item._id,
          item: item,
        };
      },
    };

    return (
      <>
        <SelectFieldAxios
          isSearch={true}
          sortBy="label"
          className="axios-select"
          placeholder="Sélectionnez un étudiant"
          tagRender={newElementSelect.tagRender}
          itemCallback={newElementSelect.itemCallback}
          item={newElementSelect}
          onSelect={newElementSelect.onSelect}
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
        />
        {studentParentRef?.current && (
          <Form.Item
            name="parent"
            hidden={true}
            initialValue={studentParentRef.current}
            rules={[
              {
                required: false,
                message: '',
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}
      </>
    );
  }, [studentParentRef, setStudentParent, studentParentRef, setCurrentStudent]);

  const getDuration = useCallback(type => {
    if (type == 'sickness') {
      return [
        {
          key: 'day',
          value: 'day',
          label: (
            <>
              <span className="label__title">Une journée</span>
            </>
          ),
        },
        {
          key: 'half-day',
          value: 'half-day',
          label: (
            <>
              <span className="label__title">Une demi-journée</span>
            </>
          ),
        },
        {
          key: 'more',
          value: 'more',
          label: (
            <>
              <span className="label__title">Un ou plusieurs cours</span>
            </>
          ),
        },
      ];
    } else {
      return [
        {
          key: 'day',
          value: 'day',
          label: (
            <>
              <span className="label__title">Une journée</span>
            </>
          ),
        },
        {
          key: 'more',
          value: 'more',
          label: (
            <>
              <span className="label__title">Plus d’une journée</span>
            </>
          ),
        },
      ];
    }
  }, []);

  const getDateOptionMedicalRepeatable = useCallback((currentDay) => {
    return (
      <>
        {!isRepeate && (
          <div
            className="is-repeate-action"
            onClick={() => {
              setIsRepeate(true);
              const newDate = getRecurringDate(recurringType, currentDay);
              AbsFormCreate.setFieldsValue({
                recurring: recurringType,
                recurring_date: newDate,
              });
              setHasRecur(false);
            }}
          >
            Absence récurrente ?
          </div>
        )}

        {isRepeate && (
          <div className="is-repeate-section">
            <div className="is-repeate-header">
              <h4 className="title">Absence récurrente</h4>
              <div
                className="is-repeate-hide"
                onClick={() => {
                  setIsRepeate(false);
                  AbsFormCreate.setFieldsValue({
                    recurring: null,
                    recurring_date: null,
                  });
                  setHasRecur(false);
                }}
              >
                Masquer récurrent
              </div>
            </div>
            <Row gutter={(15, 15)}>
              <Col sm={24} md={24} lg={12} xl={12}>
                <CustomSelect
                  label="Récurance"
                  placeholder="Selectionne"
                  name={`recurring`}
                  options={recurringOption}
                  onSelect={(LabeledValue, option) => {
                    setRecurringType(LabeledValue);
                    const newDateSet = getRecurringDate(
                      LabeledValue,
                      currentDay,
                    );
                    AbsFormCreate.setFieldsValue({
                      recurring: LabeledValue,
                      recurring_date: newDateSet,
                    });
                    setHasRecur(false);
                  }}
                />
              </Col>
              <Col sm={24} md={24} lg={12} xl={12}>
                <CustomDate
                  label="Date de fin"
                  name="recurring_date"
                  // disabledDate={disabledDateFrom}
                />
              </Col>
            </Row>
          </div>
        )}
      </>
    );
  }, [AbsFormCreate, recurringOption, isRepeate, recurringType, setHasRecur]);

  const getDateOptionMedical = useMemo(() => {
    return (
      <>
        {durationType == 'more' ? (
          <>
            <div className="form-row">
              <h5 className="form-row__label">Du</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_from"
                  //disabledDate={disabledDateFrom}
                />
                <CustomTime name="time_from" />
              </div>
            </div>
            <div className="form-row">
              <h5 className="form-row__label">Au</h5>
              <div className="form-row__fileds">
                <CustomDate name="date_to" />
                <CustomTime name="time_to" />
              </div>
            </div>
          </>
        ) : (
          <>
            <CustomDate
              name="date"
              value={currentDay}
              onChange={val => {
                setCurrentDay(val);
                setHasRecur(false);
                AbsFormCreate.setFieldsValue({
                  courses: [],
                });
              }}
            />
            {endponitObj && selectCourses(endponitObj)}
          </>
        )}

        {getDateOptionMedicalRepeatable(currentDay)}
      </>
    );
  }, [AbsFormCreate, durationType, endponitObj, currentStudent, isRepeate, hasCourse]);

  const uploadMediaFiles = useCallback(data => setMediaFiles(data), []);
  const fileOnRemove = file => {
    if (absenceInfo && absenceInfo.hasOwnProperty('media')) {
      const { media, ...objInfo } = absenceInfo;
      AbsFormCreate.setFieldsValue({
        media: [],
      });
      setMediaFiles([]);
      setAbsenceInfo(objInfo);
    }
    setMediaFiles([]);
  };
  const customItemRender = useCallback((originNode, file) => {
    const initFile = mediaFiles?.length > 0 ? mediaFiles[0] : file;
    return (
      <PreviewMedia showTitle={false} file={initFile} remove={fileOnRemove} />
    );
  }, [mediaFiles, fileOnRemove]);

  const getMediaField = useMemo(() => {
    return (
      <div className="absence-media-upload">
        <h3 className="media-title">Justificatif</h3>
        {mediaFiles && mediaFiles.length == 0 && (
          <div className="proof-option-upload">
            <UploadMedia
              name="media"
              media={mediaFiles}
              loading={uploading}
              setProgress={setUploading}
              uploadChange={uploadMediaFiles}
            />
          </div>
        )}
        {mediaFiles && mediaFiles.length > 0 && (
          <div className="form-proof">
            <Form.Item
              name="media"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              noStyle
              normalize={(value, prevValue, prevValues) => {
                if (value.length > 1) {
                  message.error(
                    `Vous ne pouvez télécharger qu'un seul fichier!`,
                  );
                  return false;
                }
              }}
            >
              <Upload
                multiple={false}
                maxCount={1}
                fileList={mediaFiles}
                onRemove={fileOnRemove}
                itemRender={customItemRender}
              />
            </Form.Item>
          </div>
        )}
      </div>
    );
  }, [mediaFiles, fileOnRemove, setUploading, uploadMediaFiles]);

  const getReason = useCallback(type => {
    const sortBy = 'label';
    const DEFAULT_REASONS =
      type == 'medical' ? DISPENSARY_PROGRAM : ACTIVITY_REASON;
    let listReasons = DEFAULT_REASONS.map(elem => {
      return {
        key: elem.key,
        label: elem.label,
        title: elem.title || elem.label,
        value: elem.value,
      };
    });
    // sort options
    listReasons.sort(sortByKey(sortBy));
    return listReasons;
  }, [DISPENSARY_PROGRAM, ACTIVITY_REASON]);

  const absFromValuesChange = useCallback(
    (values, allValues) => {
      if (values?.status) {
        setAbsenceStatus(values?.status);
      }
      if (values?.duration) {
        setDurationType(values?.duration);
        setIsRepeate(false);
        resetInitFileds(values?.duration);
      }
      if (values?.type) {
        setAbsenceType(values?.type);

        setDurationType(initAbsence?.duration);
        setIsRepeate(false);
        resetInitFileds(initAbsence?.duration);

        AbsFormCreate.setFieldsValue({
          duration: initAbsence?.duration,
        });

        if (values?.type == 'sickness') {
          setAbsenceStatus('sick');
          AbsFormCreate.setFieldsValue({
            status: 'sick',
          });
        } else {
          setAbsenceStatus('pending');
          AbsFormCreate.setFieldsValue({
            status: 'pending',
          });
        }
      }

      setTimeRangeAfterChanges(AbsFormCreate, allValues);
      setDateRangeAfterChanges(AbsFormCreate, allValues);

      setIsDisabled(false);
    },
    [initAbsence, AbsFormCreate],
  );

  return (
    <div className={childClassName}>
      <Form
        form={AbsFormCreate}
        className="form-absence__form"
        name="form_absence_create"
        initialValues={initAbsence}
        onFinish={onFinish}
        onValuesChange={absFromValuesChange}
      >
        <Row gutter={(15, 15)} style={{ marginBottom: '30px' }}>
          <Col sm={24} md={24} lg={12} xl={12}>
            {selectStudents}
          </Col>
          <Col sm={24} md={24} lg={12} xl={12}>
            <TextField
              data={{
                name: 'class',
                label: 'Class',
                disabled: true,
              }}
            />
          </Col>
          <Col sm={24} md={24} lg={24} xl={24} style={{ marginTop: '15px' }}>
            <CustomSelect
              // className="accent-label"
              label={`Type d’absence`}
              placeholder="Selectionner"
              name={`type`}
              options={getTypeList()}
            />
          </Col>
          {absenceType != 'sickness' && (
            <Col sm={24} md={24} lg={24} xl={24} style={{ marginTop: '15px' }}>
              <CustomSelect
                //className="accent-label"
                label={`Motif : ${
                  absenceType == 'medical' ? 'Médicale' : 'Non-Médicale'
                }`}
                placeholder="Selectionner"
                name={`reason`}
                options={getReason(absenceType)}
              />
            </Col>
          )}
        </Row>

        <Form.Item name="duration" label="Durée">
          <Radio.Group
            className="radio-group radio-group--horizontal"
            options={getDuration(absenceType)}
            optionType="Radio"
          />
        </Form.Item>

        {absenceType == 'sickness' ? (
          <CustomDate
            name="date"
            value={currentDay}
            onChange={val => {
              setCurrentDay(val);
              AbsFormCreate.setFieldsValue({
                courses: [],
              });
            }}
          />
        ) : (
          getDateOptionMedical
        )}

        {durationType == 'more' &&
          absenceType == 'sickness' &&
          selectCourses(endponitObj)}

        {absenceType !== 'sickness' && getMediaField}

        <CustomSelect
          className={`accent-label select-status ${absenceStatus}`}
          label={`Status`}
          valuse={absenceStatus}
          placeholder="Selectionner"
          name={`status`}
          options={getStatusesList(absenceType)}
        />

        <div className="absence-actions-wrapper" style={{ marginTop: '50px' }}>
          <Button
            className="next-btn"
            form="form_absence_create"
            htmlType="submit"
            disabled={isDisabled}
            style={{ marginLeft: 0 }}
          >
            Sauvegarder
          </Button>
        </div>
      </Form>
    </div>
  );
}

CreateAbsence.defaultProps = {};

CreateAbsence.propTypes = {
  isNew: PropTypes.bool,
  absence: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  change: PropTypes.func.isRequired,
};

export default CreateAbsence;
